import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/api/auth';
import { showToast, customToasts } from '@/utils/toast';

/**
 * Authentication Context
 * Manages user authentication state and provides auth functions
 */
const AuthContext = createContext(null);

/**
 * AuthProvider component
 * Wraps the application to provide authentication state and functions
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // Başlangıçta true - storage kontrol edilene kadar
  
  // Navigation hook for redirects
  let navigate;
  try {
    navigate = useNavigate();
  } catch (e) {
    // useNavigate not available outside Router context
    navigate = null;
  }
  
  // Initialize user from storage on mount
  useEffect(() => {
    const storedUser = sessionStorage.getItem('current_user') || localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        // Save to sessionStorage for this tab
        sessionStorage.setItem('current_user', storedUser);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
        sessionStorage.removeItem('current_user');
      }
    }
    setAuthLoading(false); // Storage kontrolü tamamlandı
  }, []);
  
  /**
   * Login with email and password
   */
  const login = async (email, password) => {
    setAuthLoading(true);
    try {
      const data = await authAPI.login(email, password);
      
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      
      // Get complete user data
      const userData = await authAPI.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.setItem('current_user', JSON.stringify(userData));
      
      setUser(userData);
      customToasts.loginSuccess(userData.name);
      
      // 🎯 Kids kullanıcısı için /study/chat sayfasına yönlendir
      if (userData.user_type === 'kids' && navigate) {
        setTimeout(() => {
          navigate('/app/study/chat');
        }, 100);
      }
      
      return userData;
    } catch (error) {
      showToast.error('Giriş Başarısız', error.message || 'Email veya şifre hatalı');
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };
  
  /**
   * Register new user account
   */
  const register = async (name, email, password, userType = 'adult') => {
    setAuthLoading(true);
    try {
      const data = await authAPI.register(name, email, password, userType);
      
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      sessionStorage.setItem('current_user', JSON.stringify(data.user));
      
      setUser(data.user);
      showToast.celebration(`Hoş Geldiniz, ${data.user.name}!`, 'Hesabınız başarıyla oluşturuldu 🎉');
      
      return data.user;
    } catch (error) {
      showToast.error('Kayıt Başarısız', error.message || 'Bir hata oluştu');
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };
  
  /**
   * Guest login - creates temporary guest account
   */
  const guestLogin = async () => {
    setAuthLoading(true);
    try {
      const data = await authAPI.guestLogin();
      
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      sessionStorage.setItem('current_user', JSON.stringify(data.user));
      
      setUser(data.user);
      showToast.info('Misafir Girişi', 'Sohbete başlayabilirsiniz!');
      
      return data.user;
    } catch (error) {
      showToast.error('Giriş Hatası', 'Misafir girişi başarısız oldu');
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };
  
  /**
   * Logout current user
   */
  const logout = () => {
    authAPI.logout();
    setUser(null);
    showToast.info('Çıkış Yapıldı', 'Görüşmek üzere!');
  };
  
  /**
   * Refresh user information from backend
   */
  const refreshUserInfo = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.setItem('current_user', JSON.stringify(userData));
    } catch (error) {
      console.error('Error refreshing user info:', error);
    }
  };
  
  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    authLoading,
    login,
    register,
    guestLogin,
    logout,
    refreshUserInfo
  }), [user, authLoading]);
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth hook
 * Access authentication context in components
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
